import type MayBePromise from '@/lib/types/MayBePromise';

import { toRefs } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { z } from 'zod';

import getCurrentUserIdAndRoleDeduplicated from '@/features/authentication/api/getCurrentUserIdAndRoleDeduplicated';
import { getCanCreateUsers } from '@/features/users/api/getCanCreateUsers';
import getIsHideProfit from '@/features/users/api/getIsHideProfit';
import { UserRole } from '@/features/users/models/users';
import useCache from '@/lib/local-storage/useCache';
import ApiResultStatus from '@/shared/api/api-result/ApiResultStatus';

export type CurrentUser = {
  readonly canCreateUsers: boolean;
  readonly id: string;
  readonly isHideProfit: boolean;
  readonly role: UserRole;
  readonly username: string;
};

type State = {
  readonly user: CurrentUser | null;
};

const defaultState = (): State => ({
  user: null,
});

const LOCAL_STORAGE_KEY = 'currentUser';

const SCHEMA_VERSION = '20.05.25';

const stateSchema = z.object({
  user: z
    .object({
      id: z.string(),
      canCreateUsers: z.boolean(),
      isHideProfit: z.boolean(),
      role: z.nativeEnum(UserRole),
      username: z.string(),
    })
    .nullable(),
});

export const useCurrentUserStore = defineStore('currentUser', () => {
  const { getValue: readState, setValue: writeState } = useCache(LOCAL_STORAGE_KEY, SCHEMA_VERSION, stateSchema);

  const state = ref<State>(readState() || defaultState());

  const updateState = (updater: (currentState: State) => State) => {
    const nextState = updater(state.value);

    state.value = {
      ...nextState,
    };

    writeState(nextState);
  };

  const loadState = async (
    options: {
      onError?: (errorMessage: string) => MayBePromise<void>;
      onNotCriticalError?: (errorMessage: string) => MayBePromise<void>;
      onOk?: () => MayBePromise<void>;
    } = {},
  ) => {
    const userIdAndRoleGettingResult = await getCurrentUserIdAndRoleDeduplicated();

    if (userIdAndRoleGettingResult.status !== ApiResultStatus.OK) {
      await options.onError?.(userIdAndRoleGettingResult.errorMessage);
      return;
    }

    const { id, role } = userIdAndRoleGettingResult;

    const [hideProfitSettingsGettingResult, canCreateUsersSettingsGettingResult] = await Promise.all([
      getIsHideProfit(id),
      getCanCreateUsers(id),
    ]);

    let isHideProfit = false;

    if (hideProfitSettingsGettingResult.status === ApiResultStatus.OK) {
      isHideProfit = hideProfitSettingsGettingResult.isHideProfit;
    } else {
      await options.onNotCriticalError?.(hideProfitSettingsGettingResult.errorMessage);
    }

    let canCreateUsers = role !== UserRole.USER;

    if (canCreateUsersSettingsGettingResult.status === ApiResultStatus.OK) {
      canCreateUsers = canCreateUsersSettingsGettingResult.canCreateUsers;
    } else {
      await options.onNotCriticalError?.(canCreateUsersSettingsGettingResult.errorMessage);
    }

    updateState((currentState) => ({
      user: {
        id,
        canCreateUsers,
        isHideProfit,
        role,
        username: currentState.user?.username ?? '%username%',
      },
    }));

    await options.onOk?.();
  };

  const init = async () => {
    await loadState();
  };

  const updateCurrentUserAfterAuthentication = async (
    username: string,
    options?: {
      onError?: (errorMessage: string) => MayBePromise<void>;
      onOk?: () => MayBePromise<void>;
    },
  ) => {
    await loadState(options);

    updateState((currentState) => {
      if (!currentState.user) {
        return currentState;
      }

      return {
        user: {
          ...currentState.user,
          username,
        },
      };
    });
  };

  const clearCurrentUser = () => {
    updateState(() => ({
      user: null,
    }));
  };

  const isHideProfit = computed(() => {
    return state.value.user?.isHideProfit ?? false;
  });

  const __deprecated__updateCurrentUser = (nextUser: { id: string; role: UserRole; username: string }) => {
    if (!state.value.user) {
      state.value = {
        user: {
          ...nextUser,
          canCreateUsers: nextUser.role !== UserRole.USER,
          isHideProfit: false,
        },
      };
      return;
    }

    state.value = {
      user: {
        ...state.value.user,
        ...nextUser,
      },
    };
  };

  return {
    ...toRefs(state),
    __deprecated__updateCurrentUser,
    clearCurrentUser,
    init,
    isHideProfit,
    updateCurrentUserAfterAuthentication,
  };
});
